import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import Seo from "@/components/Seo";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Spark } from "@/components/Doodles";

const ClaimForm = ({ sessionId, onDone }) => {
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await api.post("/auth/claim", { session_id: sessionId, name, password });
            onDone();
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        }
        setBusy(false);
    };

    return (
        <form onSubmit={submit} data-testid="claim-form" className="mx-auto mt-8 max-w-sm border-2 border-ink bg-paper p-6 text-left hard-shadow">
            <h2 className="font-display text-2xl tracking-wide text-ink">LAST STEP: YOUR MEMBER LOGIN</h2>
            <p className="mt-2 text-sm text-ink/70">
                Set a password for your account — this is how you'll book the work room and get member perks.
            </p>
            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="claim-name">Name</label>
            <input id="claim-name" data-testid="claim-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
            <label className="mt-4 block text-sm font-semibold text-ink" htmlFor="claim-password">Password</label>
            <input id="claim-password" data-testid="claim-password-input" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full border-2 border-ink bg-cream px-4 py-3 text-ink" />
            {error && <p data-testid="claim-error" className="mt-3 font-semibold text-brick">{error}</p>}
            <button data-testid="claim-submit-button" type="submit" disabled={busy} className="btn-stamp mt-5 w-full !bg-orange disabled:opacity-60">
                {busy ? "Carving your nameplate…" : "Create My Login"}
            </button>
        </form>
    );
};

const PaymentSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [state, setState] = useState({ status: "checking", tier: "", kind: "" });
    const [claimed, setClaimed] = useState(false);
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "missing", tier: "", kind: "" });
            return;
        }
        let timer;
        const poll = async () => {
            try {
                const { data } = await api.get(`/payments/status/${sessionId}`);
                if (data.payment_status === "paid") {
                    setState({ status: "paid", tier: data.tier_name, kind: data.kind });
                    return;
                }
            } catch {
                // keep polling
            }
            attempts.current += 1;
            if (attempts.current < 15) {
                timer = setTimeout(poll, 2500);
            } else {
                setState({ status: "pending", tier: "", kind: "" });
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId]);

    const headline = () => {
        if (state.kind === "studio") return "WORK ROOM RESERVED. IT'S YOURS.";
        if (state.kind === "donation") return "YOU'RE A GEM. OFFICIALLY.";
        if (state.kind === "payitforward") return "NEIGHBOR SPOTTED. YOU'RE A GEM.";
        return state.tier ? `${state.tier.toUpperCase()} STATUS: OFFICIAL` : "YOU'RE IN. OFFICIALLY.";
    };

    const bodyCopy = () => {
        if (state.kind === "studio") return "Paid, pinned, and reserved. The whole Work Room is exclusively yours for the slot you picked — meetings, making, recording, noise and all.";
        if (state.kind === "donation") return "Your donation just became chairs, paint, glue sticks, and a place for somebody who needs one. No strings — all gratitude.";
        if (state.kind === "payitforward") return "Your extra $10 a month quietly covers a neighbor's dues. No spotlight, no applause — just somebody else gets to walk through the door because of you.";
        if (claimed) return "Membership paid and your login is live. You beautiful founding weirdo — the work room calendar is ready when you are.";
        return "First dues received — you're officially a member. One more step below: create the login you'll use to book the work room.";
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-forest px-5 py-24">
            <Seo title="Payment" description="Panther City Clubhouse payment." />
            {state.status === "checking" && (
                <div data-testid="payment-checking" className="border-2 border-ink bg-cream p-10 text-center hard-shadow-lg">
                    <Spark className="mx-auto h-10 w-10 animate-spin-slow text-orange" />
                    <p className="mt-4 font-display text-3xl tracking-wide text-ink">STAMPING YOUR CARD…</p>
                    <p className="mt-2 text-ink/70">Confirming with the bank. Very official stuff.</p>
                </div>
            )}
            {state.status === "paid" && (
                <motion.div
                    data-testid="payment-success"
                    className="stamp-frame w-full max-w-lg bg-cream p-10 text-center"
                    initial={{ scale: 0.85, rotate: -4, opacity: 0 }}
                    animate={{ scale: 1, rotate: -1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 240, damping: 16 }}
                >
                    <Spark className="mx-auto h-10 w-10 text-orange" />
                    <h1 className="mt-3 font-display text-4xl tracking-wide text-forest">{headline()}</h1>
                    <p className="mt-4 leading-relaxed text-ink/80">{bodyCopy()}</p>
                    {state.kind === "membership" && !claimed && <ClaimForm sessionId={sessionId} onDone={() => setClaimed(true)} />}
                    <div className="mt-6 flex flex-wrap justify-center gap-4">
                        {(claimed || state.kind === "studio") && (
                            <Link to="/work-room" data-testid="payment-success-studio" className="btn-stamp !bg-orange">
                                Reserve the Work Room
                            </Link>
                        )}
                        <Link to="/" data-testid="payment-success-home" className="btn-stamp">
                            Back to the Clubhouse
                        </Link>
                    </div>
                    <p className="mt-4 font-hand text-2xl text-brick -rotate-2">the good chair is yours.</p>
                </motion.div>
            )}
            {(state.status === "pending" || state.status === "missing") && (
                <div data-testid="payment-pending" className="max-w-lg border-2 border-ink bg-cream p-10 text-center hard-shadow-lg">
                    <h1 className="font-display text-3xl tracking-wide text-ink">STILL PROCESSING</h1>
                    <p className="mt-3 text-ink/75">
                        The payment is taking its sweet time confirming. If you were charged, you're in — check your email, or give us a ring
                        at (682) 334-2223 and we'll sort it out.
                    </p>
                    <Link to="/membership" data-testid="payment-pending-back" className="btn-stamp mt-6">
                        Back to Membership
                    </Link>
                </div>
            )}
        </div>
    );
};

export default PaymentSuccess;
